import React, { useState, useEffect, useRef } from 'react';

// Aligné sur RapteckApp — Etape9Armoires.jsx (getSourceBTLabel)
const getSourceBTLabel = (source) => {
  if (!source) return 'Source BT';
  const nature = source.nature_designation || '';
  const lower = nature.toLowerCase();
  if (lower.includes('réseau de distribution public')) return 'Disjoncteur de Branchement';
  if (lower.includes('réseau interne')) return nature;
  if (lower.includes('groupe électrogène')) return 'Groupe électrogène';
  if (nature === 'reseau_public_bt') return 'Disjoncteur de Branchement';
  if (nature === 'transformateur_ht_bt') return 'Transformateur HT/BT';
  if (nature === 'groupe_electrogene') return 'Groupe électrogène';
  return nature || source.nature || source.localisation || 'Source BT';
};

const normalizeArmoireAmont = (value, armoires) => {
  if (!value || !value.startsWith('armoire_')) return value;
  const suffix = value.replace('armoire_', '').trim();
  const asNum = parseInt(suffix, 10);
  if (!Number.isNaN(asNum) && asNum >= 0 && asNum < armoires.length) return value;
  const idx = armoires.findIndex(a => (a?.designation || '').trim() === suffix);
  return idx >= 0 ? `armoire_${idx}` : value;
};

// Même ensemble de champs éditables que l’app principale (hors section_cable / courant_court_circuit en UI).
const EMPTY_FORM = {
  designation: '',
  localisation: '',
  code_syn: '',
  armoire_amont: '',
  rc: '',
  ik3: ''
};

const FIXED_LOCALISATION_OPTIONS = ['ATELIER', 'ATELIER MECANIQUE', 'RESERVE', 'BUREAU', 'LOCAL TECHNIQUE'];

export default function ArmoireFormModal({
  isOpen,
  armoire,
  armoireIndex,
  sourcesBT = [],
  armoires = [],
  locauxEtape10 = [],
  onSave,
  onClose
}) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const armoiresRef = useRef(armoires);
  armoiresRef.current = armoires;

  useEffect(() => {
    if (!isOpen) return;
    const currentArmoires = armoiresRef.current;
    if (armoire) {
      setFormData({
        ...EMPTY_FORM,
        ...armoire,
        armoire_amont: normalizeArmoireAmont(armoire.armoire_amont || '', currentArmoires)
      });
    } else {
      setFormData(EMPTY_FORM);
    }
  }, [isOpen, armoire, armoireIndex]);

  const getArmoiresAmontOptions = () => {
    const options = [];
    sourcesBT.forEach((source, index) => {
      options.push({
        value: `source_${index}`,
        label: getSourceBTLabel(source)
      });
    });
    armoires.forEach((arm, index) => {
      options.push({
        value: `armoire_${index}`,
        label: arm.designation || `Armoire ${index + 1}`
      });
    });
    return options;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.designation?.trim() || !formData.localisation?.trim()) {
      alert('Veuillez remplir la désignation et la localisation.');
      return;
    }
    if (!formData.armoire_amont?.trim()) {
      alert('Veuillez sélectionner une armoire amont ou une source BT (étape 8).');
      return;
    }

    const options = getArmoiresAmontOptions();
    if (options.length === 0) {
      alert('Aucune source ou armoire amont disponible. Renseignez d’abord les sources BT (étape 8).');
      return;
    }

    const payload = {
      designation: formData.designation.trim(),
      localisation: formData.localisation.trim(),
      code_syn: formData.code_syn?.trim() || '',
      armoire_amont: formData.armoire_amont.trim(),
      rc: formData.rc || '',
      ik3: formData.ik3?.trim() || ''
    };
    onSave(payload);
  };

  if (!isOpen) return null;

  const amontOptions = getArmoiresAmontOptions();

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>
            {armoire ? "✏️ Modifier l'armoire" : '➕ Nouvelle armoire'}
          </h3>
          <button type="button" onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGridThree}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Désignation <span style={styles.required}>*</span>
              </label>
              <input
                type="text"
                value={formData.designation}
                onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                placeholder="Ex: TGBT, Armoire atelier..."
                style={styles.input}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Localisation <span style={styles.required}>*</span>
              </label>
              <input
                type="text"
                value={formData.localisation}
                onChange={(e) => setFormData({ ...formData, localisation: e.target.value })}
                placeholder="Saisir ou sélectionner..."
                style={styles.input}
                list="pwa-locaux-suggestions-armoire"
              />
              <datalist id="pwa-locaux-suggestions-armoire">
                {locauxEtape10.map((local, index) => (
                  <option key={index} value={local.nom} />
                ))}
                {FIXED_LOCALISATION_OPTIONS.map((v) => (
                  <option key={v} value={v} />
                ))}
              </datalist>
              <p style={styles.hint}>
                {locauxEtape10.length > 0
                  ? `${locauxEtape10.length} local(aux) disponible(s) depuis l'étape 10`
                  : "Aucun local créé à l'étape 10"}
              </p>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Code Synoptique</label>
              <input
                type="text"
                value={formData.code_syn}
                onChange={(e) => setFormData({ ...formData, code_syn: e.target.value })}
                placeholder="Ex: T1, T2..."
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.formGridThree}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Armoire amont <span style={styles.required}>*</span>
              </label>
              <select
                value={formData.armoire_amont}
                onChange={(e) => setFormData({ ...formData, armoire_amont: e.target.value })}
                style={styles.select}
              >
                <option value="" disabled>
                  Sélectionner une source ou armoire
                </option>
                {amontOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>RC (Ohm)</label>
              <select
                value={formData.rc}
                onChange={(e) => setFormData({ ...formData, rc: e.target.value })}
                style={styles.select}
              >
                <option value="">Sélectionner</option>
                <option value="<2">&lt;2</option>
                <option value="<3">&lt;3</option>
                <option value="<5">&lt;5</option>
                <option value="<10">&lt;10</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>IK3 (kA)</label>
              <input
                type="text"
                value={formData.ik3}
                onChange={(e) => setFormData({ ...formData, ik3: e.target.value.trim() })}
                placeholder="Ex: 25, 36"
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.buttons}>
            <button type="button" onClick={onClose} style={styles.cancelButton}>
              Annuler
            </button>
            <button type="submit" style={styles.saveButton}>
              {armoire ? 'Mettre à jour' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px'
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '20px',
    width: '100%',
    maxWidth: '560px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '2px solid #f1f5f9',
    position: 'sticky',
    top: 0,
    backgroundColor: 'white',
    zIndex: 1
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#1e293b',
    margin: 0
  },
  closeButton: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: '#f1f5f9',
    fontSize: '20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  form: {
    padding: '20px'
  },
  formGridThree: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '8px'
  },
  formGroup: {
    marginBottom: '12px',
    flex: '1 1 140px',
    minWidth: '120px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#475569',
    marginBottom: '8px'
  },
  required: {
    color: '#ef4444'
  },
  hint: {
    fontSize: '12px',
    color: '#64748b',
    marginTop: '6px',
    marginBottom: 0
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box'
  },
  select: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    outline: 'none',
    backgroundColor: 'white',
    boxSizing: 'border-box'
  },
  buttons: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px'
  },
  cancelButton: {
    flex: 1,
    padding: '14px',
    fontSize: '16px',
    fontWeight: '600',
    backgroundColor: 'white',
    color: '#64748b',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    cursor: 'pointer'
  },
  saveButton: {
    flex: 1,
    padding: '14px',
    fontSize: '16px',
    fontWeight: '600',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
  }
};
